import type {
	ActivateProjectsResponse,
	DeactivateProjectsResponse,
	DeleteProjectsResponse,
	Project,
	ProjectFilters,
	ProjectsResponse,
} from '@/types/project'
import { demoApi } from './db'

/** Cloud GET /v1/users response: result is Record<userId, { name, email, roles }>. */
interface CloudUsersResult {
	result?: Record<string, { name: string; email: string; roles: number[] }>
}

/** Fetch actual user count per tenant: one GET /v1/users per tenant with x-bytebeam-tenant. */
async function getUsersCountPerTenant(tenantIds: string[]): Promise<Record<string, number>> {
	const counts: Record<string, number> = Object.fromEntries(tenantIds.map(id => [id, 0]))
	await Promise.all(
		tenantIds.map(async tenantId => {
			try {
				const res = (await demoApi.getWithTenantId('/v1/users', tenantId)) as CloudUsersResult
				const result = res?.result
				counts[tenantId] =
					result && typeof result === 'object' && !Array.isArray(result) ? Object.keys(result).length : 0
			} catch {
				// leave 0
			}
		}),
	)
	return counts
}

/** Fetch actual device count per tenant using the same API as deviceApi.getDevicesCount(key, '', 'active'). */
async function getDevicesCountPerTenant(tenantIds: string[]): Promise<Record<string, number>> {
	const counts: Record<string, number> = Object.fromEntries(tenantIds.map(id => [id, 0]))
	await Promise.all(
		tenantIds.map(async tenantId => {
			try {
				const res = (await demoApi.getWithTenantId('/v1/devices/count?status=active', tenantId)) as { count?: string }
				const n = Number.parseInt(res?.count ?? '0', 10)
				counts[tenantId] = Number.isNaN(n) ? 0 : n
			} catch {
				// leave 0
			}
		}),
	)
	return counts
}

interface TenantRow {
	id: string
	name: string
	created_at: string
	created_by: string
	deactivated_at: string | null
	deactivated_by: string | null
	frontend_settings?: Record<string, unknown>
	is_org?: boolean
}

/** Bytes per IoT data point for Clickhouse (deterministic). */
const BYTES_PER_POINT_CLICKHOUSE = 256
/** Multiplier: device count → IoT data points (e.g. 1500 points per device). */
const IOT_POINTS_PER_DEVICE = 1500000
/** Lake is always this factor > Clickhouse (e.g. 1.5x). */
const LAKE_FACTOR = 1.5

function dataSizesFromDeviceCount(deviceCount: number): { dataSizeClickhouse: string; dataSizeLake: string } {
	const iotPoints = deviceCount * IOT_POINTS_PER_DEVICE
	const clickhouseBytes = iotPoints * BYTES_PER_POINT_CLICKHOUSE
	const clickhouseGB = clickhouseBytes / 1e9
	const lakeGB = clickhouseGB * LAKE_FACTOR
	return {
		dataSizeClickhouse: `${clickhouseGB.toFixed(2)} GB`,
		dataSizeLake: `${lakeGB.toFixed(2)} GB`,
	}
}

function transformTenantToProject(t: TenantRow, userCount: number, deviceCount: number): Project {
	const { dataSizeClickhouse, dataSizeLake } = dataSizesFromDeviceCount(deviceCount)
	return {
		id: t.id,
		name: t.name || t.id,
		status: t.deactivated_at ? 'deactivated' : 'active',
		users: userCount,
		dataSizeClickhouse,
		dataSizeLake,
		createdAt: t.created_at,
		updatedAt: undefined,
		frontend_settings: t.frontend_settings,
		is_org: t.is_org,
	}
}

export const projectsApi = {
	listProjects: async (
		organizationId: string,
		filters: ProjectFilters = {},
		page = 1,
		limit = 10,
	): Promise<ProjectsResponse> => {
		try {
			const projectsRes = await demoApi.get<{ tenants: TenantRow[] }>(`/projects/${encodeURIComponent(organizationId)}`)
			const tenantIds = projectsRes.tenants.map(t => t.id)
			const [usersCountByTenant, devicesCountByTenant] = await Promise.all([
				getUsersCountPerTenant(tenantIds),
				getDevicesCountPerTenant(tenantIds),
			])
			let tenants = projectsRes.tenants.map(t => {
				const userCount = usersCountByTenant[t.id] ?? 0
				const deviceCount = devicesCountByTenant[t.id] ?? 0
				return transformTenantToProject(t, userCount, deviceCount)
			})

			// Compute aggregate counts before applying filters/pagination so tab badges are accurate.
			const allCount = tenants.length
			const activeCount = tenants.filter(t => t.status === 'active').length
			const deactivatedCount = tenants.filter(t => t.status === 'deactivated').length

			// Store unfiltered data for allData
			const allData = [...tenants]

			// Apply filters
			if (filters.search) {
				const searchLower = filters.search.toLowerCase()
				tenants = tenants.filter(
					t => t.name.toLowerCase().includes(searchLower) || t.id.toLowerCase().includes(searchLower),
				)
			}
			if (filters.status) {
				tenants = tenants.filter(t => t.status === filters.status)
			}
			if (filters.name && filters.name.length > 0) {
				const names = filters.name
				tenants = tenants.filter(t => names.includes(t.name))
			}
			if (filters.id && filters.id.length > 0) {
				const ids = new Set(filters.id)
				tenants = tenants.filter(t => ids.has(t.id))
			}
			if (filters.usersMin !== undefined) {
				tenants = tenants.filter(t => t.users >= (filters.usersMin as number))
			}
			if (filters.usersMax !== undefined) {
				tenants = tenants.filter(t => t.users <= (filters.usersMax as number))
			}

			if (filters.sortBy) {
				tenants.sort((a, b) => {
					const aVal = a[filters.sortBy as keyof Project] as string | number
					const bVal = b[filters.sortBy as keyof Project] as string | number
					const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
					return filters.sortOrder === 'desc' ? -comparison : comparison
				})
			}

			const total = tenants.length
			const paginated = tenants.slice((page - 1) * limit, page * limit)

			return {
				allData,
				data: paginated,
				pagination: { page, limit, total, totalPages: Math.ceil(total / limit) },
				counts: {
					all: allCount,
					active: activeCount,
					deactivated: deactivatedCount,
				},
			}
		} catch (error) {
			console.error('Failed to fetch projects:', error)
		}

		// Fallback if API fails
		return {
			allData: [],
			data: [],
			pagination: { page, limit, total: 0, totalPages: 0 },
			counts: {
				all: 0,
				active: 0,
				deactivated: 0,
			},
		}
	},
	getProjectDetails: async (organizationId: string, projectId: string): Promise<Project> => {
		const [projectRes, usersCountByTenant, devicesCountByTenant] = await Promise.all([
			demoApi.get<{ tenant: TenantRow }>(
				`/projects/${encodeURIComponent(organizationId)}/${encodeURIComponent(projectId)}`,
			),
			getUsersCountPerTenant([projectId]),
			getDevicesCountPerTenant([projectId]),
		])
		const userCount = usersCountByTenant[projectId] ?? 0
		const deviceCount = devicesCountByTenant[projectId] ?? 0
		return transformTenantToProject(projectRes.tenant, userCount, deviceCount)
	},
	createProject: async (
		organizationId: string,
		tenantName: string,
		frontend_settings?: Record<string, unknown>,
	): Promise<{ id: string }> => {
		const res = await demoApi.postNoTenant<{ id: string }>(`/projects/${encodeURIComponent(organizationId)}`, {
			name: tenantName,
			...(frontend_settings ? { frontend_settings } : {}),
		})
		return { id: res.id }
	},
	updateProject: async (_organizationId: string, tenantId: string, name: string): Promise<Project> => {
		try {
			await demoApi.put(`/projects/${encodeURIComponent(_organizationId)}/${encodeURIComponent(tenantId)}`, { name })
		} catch (error) {
			console.error('Failed to update project:', error)
		}

		const { dataSizeClickhouse, dataSizeLake } = dataSizesFromDeviceCount(0)
		return {
			id: tenantId,
			name: name,
			status: 'active',
			users: 0,
			dataSizeClickhouse,
			dataSizeLake,
			createdAt: new Date().toISOString(),
		}
	},
	activateProjects: async (_organizationId: string, projectIds: string[]): Promise<ActivateProjectsResponse> => {
		try {
			await demoApi.post('/projects/activate', { projectIds })
		} catch (error) {
			console.error('Failed to activate projects:', error)
		}
		return { success: true, activatedCount: projectIds.length, message: 'Activated' }
	},
	deactivateProjects: async (_organizationId: string, projectIds: string[]): Promise<DeactivateProjectsResponse> => {
		try {
			await demoApi.post('/projects/deactivate', { projectIds })
		} catch (error) {
			console.error('Failed to deactivate projects:', error)
		}
		return { success: true, deactivatedCount: projectIds.length, message: 'Deactivated' }
	},
	deleteProjects: async (_organizationId: string, projectIds: string[]): Promise<DeleteProjectsResponse> => {
		try {
			await demoApi.post('/projects/delete', { projectIds })
		} catch (error) {
			console.error('Failed to delete projects:', error)
		}
		return { success: true, deletedCount: projectIds.length, message: 'Deleted' }
	},
	/** Lightweight project name lookup — no user/device counts, no pagination. */
	listProjectNames: async (organizationId: string): Promise<Map<string, string>> => {
		try {
			const projectsRes = await demoApi.get<{ tenants: TenantRow[] }>(`/projects/${encodeURIComponent(organizationId)}`)
			return new Map(projectsRes.tenants.map(t => [t.id, t.name || t.id]))
		} catch (error) {
			console.error('Failed to fetch project names:', error)
			return new Map()
		}
	},
	updateProjectFrontendSettings: async (tenantId: string, frontendSettings: Record<string, unknown>): Promise<void> => {
		try {
			await demoApi.patch(
				'/projects/tenant-settings',
				{ frontend_settings: frontendSettings },
				{
					headers: { 'x-bytebeam-tenant': tenantId },
				},
			)
		} catch (error) {
			console.error('Failed to update frontend settings:', error)
			throw error
		}
	},
}
